import React from "react";
import { localizeWithMemberName } from "@/main/react/component/localizeWithMemberName";
import { Button } from "@/components/ui/button";
import type { Localizer } from "@/main/react/Types";
import { HANDOFF_VARIANT_TITLE_EXCERPT, type HandoffCandidate } from "@/main/common/HandoffTypes";
import { onHandoffCardActivated } from "@/main/analytics/feedAttribution";

/**
 * The empty-room hand-off card (`designs/01-now/2026-09-06-empty-room-handoff-design.md` §2, §5): right
 * after publishing, the author is offered ONE unanswered post by somebody else.
 *
 * <p>An offer, not an obligation. No close control and no state (D14: it is rendered from the one
 * response the page fetched after landing, and a refresh does not re-fetch); no counts, no queue
 * language, nothing that frames another member's silence as a debt (rule 12, tenet T5). One quiet
 * bordered card (flat-X: a hairline, no shadow), one CTA — a real anchor, so middle-click and
 * open-in-new-tab behave and it survives the island being unmounted.
 *
 * <p>Both hooks are `data-playwright-id` literals on purpose: the control census registry is GENERATED
 * from them (`scripts/emit-control-census-registry.mjs`), which is what makes the card's impression
 * and click measurable without a `UserActivityEventType` (design §7).
 */
/**
 * The one-shot marker the CTA carries (D5/A3, design §7): `DiscussionPage` consumes it on mount and the
 * reply composer sends `surface: "handoff"` on the publish request. Appended with `&` when the permalink
 * already has a query, so a future `url` with parameters keeps them.
 */
export const HANDOFF_FROM_PARAM = "from";
export const HANDOFF_FROM_VALUE = "handoff";

export function withHandoffMarker(url: string): string {
    const [beforeHash, hash] = url.split("#", 2);
    const joiner = beforeHash.includes("?") ? "&" : "?";
    return `${beforeHash}${joiner}${HANDOFF_FROM_PARAM}=${HANDOFF_FROM_VALUE}${hash !== undefined ? `#${hash}` : ""}`;
}

export function HandoffCard(props: { candidate: HandoffCandidate; localizer: Localizer }) {
    const { candidate, localizer } = props;
    // FI-200 (round 31 D2, D13): the copy A/B. The arm is decided (and its exposure recorded) server-side on
    // the 200 that rendered this card. Only the one named variant is B; `control`, an absent field (a
    // backend that predates it) and any unknown value all render today's card.
    const showsPost = candidate.variant === HANDOFF_VARIANT_TITLE_EXCERPT;
    const excerpt = candidate.excerpt?.trim();
    return (
        <section
            data-playwright-id="handoff-card"
            data-handoff-discussion-id={candidate.discussionPublicId}
            data-handoff-variant={showsPost ? HANDOFF_VARIANT_TITLE_EXCERPT : "control"}
            aria-labelledby="handoff-card-title"
            className="flex w-full flex-col gap-2 rounded-xl border border-border bg-card px-4 py-3"
        >
            <h2 id="handoff-card-title" className="text-base font-medium">
                {localizer("handoff.card.title")}
            </h2>
            {showsPost ? (
                <div className="flex min-w-0 flex-col gap-0.5">
                    {/* Member content, so it is not a heading of the page's outline and keeps the
                        browser translator's default behaviour, like every post title on a card. */}
                    <p className="min-w-0 wrap-anywhere text-sm font-semibold text-foreground" data-playwright-id="handoff-card-post-title">
                        {candidate.title}
                    </p>
                    {excerpt ? (
                        <p className="min-w-0 wrap-anywhere text-sm text-foreground line-clamp-1" data-playwright-id="handoff-card-post-excerpt">
                            {excerpt}
                        </p>
                    ) : null}
                </div>
            ) : null}
            <p className="text-sm text-muted-foreground" data-playwright-id="handoff-card-body">
                {showsPost
                    ? localizer("handoff.card.body_untitled_reply")
                    : localizeWithMemberName(localizer, "handoff.card.body", candidate.authorUsername)}
            </p>
            <div>
                <Button asChild size="sm" className="rounded-full">
                    <a
                        href={withHandoffMarker(candidate.url)}
                        data-playwright-id="handoff-card-cta"
                        // FI-139: attribute the view this click opens (`discussion_views.source =
                        // 'handoff'`), independent of the `?from=handoff` marker, which the publish
                        // surface consumes.
                        onClick={() => onHandoffCardActivated(candidate.discussionPublicId)}
                    >
                        {localizer("handoff.card.cta")}
                    </a>
                </Button>
            </div>
        </section>
    );
}

export default HandoffCard;
